// Phase 2D.1C — organization lifecycle, client side.
//
// The backend is authoritative: it blocks every tenant request of a non-active
// organization on its own. This module only decides what the person SEES, so the
// app shows a clear Bulgarian explanation instead of a wall of failed requests.
//
// Deliberately dependency-free and side-effect-free, so the boundary cases can
// be executed for real in node by the test suite.

/** Fired once when a tenant request was refused for a lifecycle reason. */
export const ORG_BLOCKED_EVENT = "offertrace:organizationBlocked";

/** The stable codes the backend answers with. Never a lifecycle enum label.
 *
 *  Phase 2D.2 checkpoint 3 adds ``membership_inactive``: a deactivated member
 *  keeps a valid session for up to an hour, so the refusal must trigger the same
 *  refresh-and-explain path as a blocked organization instead of a wall of 403s.
 */
export const BLOCKED_CODES = [
  "organization_inactive",
  "organization_unavailable",
  "membership_inactive",
];

/** Missing lifecycle data is treated as active: that is the pre-phase answer,
 *  and the backend still refuses the request if it is wrong. */
export function isOrgActive(org) {
  if (!org) return false;
  return org.is_active !== false;
}

/** Bulgarian copy for a non-active organization, server text first. */
export function orgLifecycleCopy(org, fallback) {
  const f = fallback || {};
  const title = (org && typeof org.status_title === "string" && org.status_title.trim()) || "";
  const message = (org && typeof org.status_message === "string" && org.status_message.trim()) || "";
  return {
    title: title || f.title || "",
    message: message || f.message || "",
  };
}

/** Which organization to select after a memberships refresh.
 *
 *  An explicit current choice is kept, so a person never gets silently moved to
 *  another organization. Otherwise the first ACTIVE membership wins, and only if
 *  every membership is blocked does the first one win — the person then sees the
 *  explanation instead of an empty screen.
 */
export function pickDefaultOrgId(list, currentId) {
  const orgs = Array.isArray(list) ? list : [];
  if (!orgs.length) return null;
  if (currentId && orgs.some((o) => o && o.id === currentId)) return currentId;
  const active = orgs.find((o) => isOrgActive(o));
  return (active || orgs[0]).id || null;
}

/** True only for a 403 that a lifecycle gate produced. */
export function isOrganizationBlockedError(error) {
  const response = error && error.response;
  if (!response || response.status !== 403) return false;
  const detail = response.data && response.data.detail;
  const code = detail && typeof detail === "object" ? detail.code : detail;
  return BLOCKED_CODES.indexOf(code) !== -1;
}
